import * as React from 'react'
import { observer, inject } from 'mobx-react'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import { API, IBaseProduct, INestedProduct } from '@getgreenline/homi-shared'
import { ProductAsyncInput } from '../../../../../components/ProductAsyncInput'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { message, Radio, Input } from 'antd'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { CreateProduct } from '../ProductStore'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  productId: string
  onClose: () => void
  onSubmit: (product: INestedProduct) => void
  isParentBatchTracked: boolean
}

interface State {
  targetProduct?: IBaseProduct
  parentType: ParentType
  newParentName?: string
}

export enum ParentType {
  EXISTING = 'existing',
  NEW = 'new',
}

@inject('currentCompanyStore')
@observer
export class MergeProductModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      parentType: ParentType.EXISTING,
    }
  }

  mergeToParent = async () => {
    const { parentType, newParentName } = this.state

    if (parentType === ParentType.NEW) {
      if (!newParentName) {
        return message.error('Please enter a new parent product name')
      }

      try {
        const company = this.props.currentCompanyStore!.company!
        const childProduct = await API.getProductById(company.id, this.props.productId)

        const product = new CreateProduct()
        product.setName(newParentName)
        product.setCategoryId(childProduct.categoryId)
        product.setSupplierId(childProduct.supplierId)
        product.setTaxGroupId(childProduct.taxGroupId)
        product.setIsBatchTracked(childProduct.isBatchTracked)

        const createObject = { ...product.networkObject, trackInventory: false }
        const newParentProduct = await API.addProduct(company.id, createObject)
        this.setState({ targetProduct: newParentProduct })
      } catch (error) {
        return message.error(
          parseErrorMsg(error) || 'There was an error creating your new parent product',
        )
      }
    }

    this.submit()
  }

  async submit() {
    try {
      if (this.state.targetProduct) {
        if (this.state.targetProduct.isBatchTracked !== this.props.isParentBatchTracked) {
          return message.error('Lot tracking setting should be the same before merging')
        }

        const company = this.props.currentCompanyStore!.company!
        const newProduct = await API.mergeProduct(
          company.id,
          this.props.productId,
          this.state.targetProduct.id,
        )
        this.props.onSubmit(newProduct)
        message.success('Product successfully merged')
      }
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error converting this variant.')
    }
  }

  get isMergeButtonDisabled(): boolean {
    return this.state.parentType === ParentType.EXISTING
      ? !this.state.targetProduct
      : !this.state.newParentName
  }

  render() {
    return (
      <Modal isOpen={true} toggle={() => this.props.onClose()}>
        <ModalHeader toggle={() => this.props.onClose()}>Merge</ModalHeader>
        <ModalBody>
          <div className='text-muted mb-3'>Merge product with:</div>
          <Radio.Group
            onChange={(e) => this.setState({ parentType: e.target.value })}
            value={this.state.parentType}
            style={{ display: 'flex', justifyContent: 'space-around' }}
            className='mb-2'
          >
            <Radio value={ParentType.EXISTING}>Existing parent product</Radio>
            <Radio value={ParentType.NEW}>New parent product</Radio>
          </Radio.Group>

          <div style={{ padding: 10 }}>
            {this.state.parentType === ParentType.EXISTING && (
              <ProductAsyncInput
                placeholder='Select parent product'
                optionFilterFunction={(products) => {
                  return products.filter((p) => p.isMasterProduct)
                }}
                value={this.state.targetProduct ? this.state.targetProduct.id : undefined}
                onSelect={(product) => {
                  this.setState({ targetProduct: product })
                }}
              />
            )}

            {this.state.parentType === ParentType.NEW && (
              <>
                <label>
                  Name<em className='text-danger'> * required</em>
                </label>
                <Input
                  value={this.state.newParentName}
                  placeholder='Enter new parent product name'
                  onChange={(e) => this.setState({ newParentName: e.target.value })}
                />
              </>
            )}
          </div>

          <br />
          <div className='text-muted'>
            Merging will move this product into another product as a variant.
          </div>
          <div className='text-muted mb-3'>
            Your inventory and sales history will not be affected.
          </div>
          <BlazeButton
            buttonProps={{
              className: 'w-100',
              type: 'primary',
              size: 'large',
              onClick: () => this.mergeToParent(),
              disabled: this.isMergeButtonDisabled,
            }}
            titleProps={{
              title: 'Merge',
            }}
          />
        </ModalBody>
      </Modal>
    )
  }
}
